import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, PageHeader, Btn, Input } from "@/components/admin/ui";

export default function Settings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const isSuper = user?.role === "super_admin";

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);

  const save = async () => {
    try {
      await api.patch("/settings", {
        organization: s.organization, reporting_currency: s.reporting_currency,
        earning_rules: s.earning_rules, inactivity_minutes: Number(s.inactivity_minutes),
        online_minutes: Number(s.online_minutes), alert_thresholds: s.alert_thresholds,
        working_hours: s.working_hours,
      });
      toast.success("Settings saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const reseed = async () => {
    if (!window.confirm("This will delete all demo data and reseed. Continue?")) return;
    try { const { data } = await api.post("/admin/reseed"); toast.success(`Reseeded · ${data.workers} workers`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!s) return <p className="text-slate-400">Loading settings…</p>;

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader title="System Settings" subtitle="Organization, KPIs, currency, alerts and working hours" />
      <Card className="p-6 space-y-4">
        <h3 className="font-head font-semibold text-white">Organization</h3>
        <Input label="Organization name" value={s.organization || ""} onChange={(e) => setS({ ...s, organization: e.target.value })} disabled={!isSuper} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Reporting currency" value={s.reporting_currency} onChange={(e) => setS({ ...s, reporting_currency: e.target.value })} disabled={!isSuper} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Online (min)" type="number" value={s.online_minutes} onChange={(e) => setS({ ...s, online_minutes: e.target.value })} disabled={!isSuper} />
            <Input label="Inactive (min)" type="number" value={s.inactivity_minutes} onChange={(e) => setS({ ...s, inactivity_minutes: e.target.value })} disabled={!isSuper} />
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <h3 className="font-head font-semibold text-white">Earning Rules (INR)</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Input label="Per task" type="number" value={s.earning_rules.per_task} onChange={(e) => setS({ ...s, earning_rules: { ...s.earning_rules, per_task: Number(e.target.value) } })} disabled={!isSuper} />
          <Input label="Per lead" type="number" value={s.earning_rules.per_lead} onChange={(e) => setS({ ...s, earning_rules: { ...s.earning_rules, per_lead: Number(e.target.value) } })} disabled={!isSuper} />
          <Input label="Per meeting" type="number" value={s.earning_rules.per_meeting} onChange={(e) => setS({ ...s, earning_rules: { ...s.earning_rules, per_meeting: Number(e.target.value) } })} disabled={!isSuper} />
          <Input label="Sale comm %" type="number" value={s.earning_rules.sale_commission_pct} onChange={(e) => setS({ ...s, earning_rules: { ...s.earning_rules, sale_commission_pct: Number(e.target.value) } })} disabled={!isSuper} />
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <h3 className="font-head font-semibold text-white">Alert Thresholds</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Input label="Worker daily %" type="number" value={s.alert_thresholds.worker_daily_pct} onChange={(e) => setS({ ...s, alert_thresholds: { ...s.alert_thresholds, worker_daily_pct: Number(e.target.value) } })} disabled={!isSuper} />
          <Input label="Team weekly %" type="number" value={s.alert_thresholds.team_weekly_pct} onChange={(e) => setS({ ...s, alert_thresholds: { ...s.alert_thresholds, team_weekly_pct: Number(e.target.value) } })} disabled={!isSuper} />
          <Input label="Campaign %" type="number" value={s.alert_thresholds.campaign_pct} onChange={(e) => setS({ ...s, alert_thresholds: { ...s.alert_thresholds, campaign_pct: Number(e.target.value) } })} disabled={!isSuper} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Working hours start" value={s.working_hours.start} onChange={(e) => setS({ ...s, working_hours: { ...s.working_hours, start: e.target.value } })} disabled={!isSuper} />
          <Input label="Working hours end" value={s.working_hours.end} onChange={(e) => setS({ ...s, working_hours: { ...s.working_hours, end: e.target.value } })} disabled={!isSuper} />
        </div>
      </Card>

      {isSuper && (
        <div className="flex items-center justify-between">
          <Btn data-testid="settings-save" onClick={save}>Save Settings</Btn>
          <Btn variant="danger" data-testid="settings-reseed" onClick={reseed}>Wipe & Reseed Demo Data</Btn>
        </div>
      )}
    </div>
  );
}
